"use client";

import type { Voice } from "@/app/_data/voices";
import type { TextEditorRef } from "@ui/components/core/TextEditor/types";

import { Avatar } from "@ui/components/core/Avatar";
import { Button } from "@ui/components/core/Button";
import TextEditor from "@ui/components/core/TextEditor";
import { getVoiceImage } from "@ui/components/helpers/voiceUtils";
import { cn } from "@ui/lib/utils";
import { PlusIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

export interface MultiSpeakerSample {
  id: string;
  speakerName: string;
  text: string;
  voiceId: string;
}

export interface MultiSpeakerInputProps {
  /** Available voices for selection */
  availableVoices: Voice[];
  /** Custom className */
  className?: string;
  /** Optional initial samples to display */
  initialSamples?: MultiSpeakerSample[];
  /** Maximum character limit per text area */
  maxCharacters?: number;
  /** Callback when samples change */
  onChange?: (samples: MultiSpeakerSample[]) => void;
  /** Callback to notify parent of changes */
  onDirty?: () => void;
  /** Callback to notify parent of changes compared to initial samples */
  setHasChanges?: (hasChanges: boolean) => void;
  /** Variant */
  variant?: "default" | "simple";
}

export interface MultiSpeakerInputRef {
  /** Add a new speaker */
  addSpeaker: () => void;
  /** Clear all samples */
  clear: () => void;
  /** Get current samples */
  getSamples: () => MultiSpeakerSample[];
  /** Set samples programmatically */
  setSamples: (samples: MultiSpeakerSample[]) => void;
}

const CharacterCounter = ({
  text,
  maxCharacters,
  className,
}: {
  className?: string;
  maxCharacters: number;
  text: string;
}) => {
  const isNearLimit = text.length > maxCharacters * 0.8;
  const isOverLimit = text.length > maxCharacters;

  return (
    <div
      className={cn(
        "pointer-events-none absolute bottom-4 right-16 flex items-center justify-center rounded-full px-8 py-2 shadow-xs transition-opacity duration-200",
        isNearLimit ? "bg-negative opacity-100" : "opacity-0",
        isOverLimit && "bg-red text-inverse",
        className,
      )}
    >
      <span className='f-ui-05 tabular-nums'>
        {text.length}/{maxCharacters}
      </span>
    </div>
  );
};

const SpeakerItem = ({
  sample,
  availableVoices,
  onUpdate,
  onRemove,
  canRemove,
  maxCharacters,
  textareaRef,
  index,
  variant = "default",
}: {
  availableVoices: Voice[];
  canRemove: boolean;
  index: number;
  maxCharacters: number;
  onRemove: (id: string, deletedIndex: number) => void;
  onUpdate: (id: string, updates: Partial<MultiSpeakerSample>) => void;
  sample: MultiSpeakerSample;
  textareaRef: React.RefObject<TextEditorRef>;
  variant?: "default" | "simple";
}) => {
  const t = useTranslations("ClientKeys.TTS.multi_speaker");

  const selectedVoice = availableVoices.find(voice => voice.voiceId === sample.voiceId);

  return (
    <div className={cn("mb-8 flex flex-col px-16", variant === "simple" && "px-0")}>
      {/* Voice display */}
      <div className='mb-8 flex items-center gap-12'>
        {variant === "default" && (
          <Avatar className='h-20 w-20' height={14} image={getVoiceImage(selectedVoice, "md")} size='sm' width={14} />
        )}
        <span className={cn(variant === "simple" ? "f-ui-03 text-inverse-light opacity-70" : "f-ui-04 font-medium")}>
          {sample.speakerName}
        </span>
      </div>

      {/* Text area */}
      <div className='relative'>
        <TextEditor
          className={cn("h-52 w-full resize-none outline-none", variant === "simple" && "h-auto")}
          onChange={text => onUpdate(sample.id, { text })}
          onKeyDown={e => {
            // Delete speaker if textarea is empty and backspace is pressed
            if (e.key === "Backspace" && sample.text === "" && canRemove) {
              e.preventDefault();
              onRemove(sample.id, index);
            }
          }}
          placeholder={t("text_placeholder", { speakerName: sample.speakerName })}
          ref={textareaRef}
          value={sample.text}
          withBrackets={variant === "default"}
        />
        <CharacterCounter maxCharacters={maxCharacters} text={sample.text} />
      </div>
    </div>
  );
};

export const MultiSpeakerInput = forwardRef<MultiSpeakerInputRef, MultiSpeakerInputProps>(
  (
    {
      initialSamples = [],
      availableVoices,
      onChange,
      maxCharacters = 1000,
      className,
      setHasChanges,
      onDirty,
      variant = "default",
    },
    ref,
  ) => {
    const t = useTranslations("ClientKeys.TTS.multi_speaker");
    const tGlobal = useTranslations("ClientKeys.Globals");

    const [samples, setSamples] = useState<MultiSpeakerSample[]>(() => {
      if (initialSamples.length > 0) {
        return initialSamples;
      }

      // Create initial sample if none provided
      const firstVoice = availableVoices[0];
      if (firstVoice) {
        return [
          {
            id: `sample-${Date.now()}`,
            text: "",
            speakerName: t("speaker_name", { number: 1 }),
            voiceId: firstVoice.voiceId,
          },
        ];
      }

      return [];
    });

    // Sync samples with initialSamples when it changes
    useEffect(() => {
      if (initialSamples.length > 0) {
        setSamples(initialSamples);
      } else if (availableVoices.length > 0 && samples.length === 0) {
        // Only create default sample if we have no samples and no initial samples
        const firstVoice = availableVoices[0];
        setSamples([
          {
            id: `sample-${Date.now()}`,
            text: "",
            speakerName: t("speaker_name", { number: 1 }),
            voiceId: firstVoice.voiceId,
          },
        ]);
      }
    }, [initialSamples, availableVoices, t]);

    // Create refs for each textarea
    const textareaRefs = useRef<React.RefObject<TextEditorRef>[]>([]);
    const [focusIndex, setFocusIndex] = useState<null | number>(null);

    // Track the baseline initial samples for change detection
    const baselineInitialSamples = useRef<MultiSpeakerSample[]>(initialSamples);

    // Update baseline when initialSamples changes
    useEffect(() => {
      baselineInitialSamples.current = initialSamples;
    }, [initialSamples]);

    // Update refs array when samples change
    useEffect(() => {
      textareaRefs.current = samples.map((_, i) => textareaRefs.current[i] || { current: null });
    }, [samples.length]);

    // Handle focus after deletion
    useEffect(() => {
      if (focusIndex !== null && textareaRefs.current[focusIndex]?.current) {
        const textarea = textareaRefs.current[focusIndex].current;
        if (textarea) {
          textarea.focus();
          textarea.setCursorPosition(textarea.value?.length ?? 0);
        }
        setFocusIndex(null);
      }
    }, [focusIndex, samples]);

    // Helper function to compare samples for changes
    const samplesHaveChanged = useCallback(
      (currentSamples: MultiSpeakerSample[], initialSamples: MultiSpeakerSample[]) => {
        if (currentSamples.length !== initialSamples.length) {
          return true;
        }

        return currentSamples.some((current, index) => {
          const initial = initialSamples[index];
          if (!initial) return true;

          return (
            current.text !== initial.text ||
            current.voiceId !== initial.voiceId ||
            current.speakerName !== initial.speakerName
          );
        });
      },
      [],
    );

    // Update parent when samples change
    useEffect(() => {
      onChange?.(samples);
      // Notify parent of changes compared to initial samples
      if (setHasChanges) {
        const hasChanges = samplesHaveChanged(samples, baselineInitialSamples.current);
        setHasChanges(hasChanges);
      }
    }, [samples, onChange, setHasChanges, samplesHaveChanged]);

    const updateSample = useCallback(
      (id: string, updates: Partial<MultiSpeakerSample>) => {
        setSamples(prev => prev.map(sample => (sample.id === id ? { ...sample, ...updates } : sample)));
        onDirty?.();
      },
      [onDirty],
    );

    const removeSample = useCallback(
      (id: string, deletedIndex: number) => {
        setSamples(prev => {
          const newSamples = prev.filter(sample => sample.id !== id);

          // Determine which textarea to focus after deletion
          if (newSamples.length > 0) {
            if (deletedIndex === 0) {
              // If we deleted the first item, focus the new first item (index 0)
              setFocusIndex(0);
            } else {
              // Otherwise, focus the previous item (deletedIndex - 1)
              setFocusIndex(deletedIndex - 1);
            }
          }

          return newSamples;
        });
        onDirty?.();
      },
      [onDirty],
    );

    const addSpeaker = useCallback(() => {
      if (availableVoices.length === 0) return;

      const newSpeakerNumber = samples.length + 1;

      // Cycle voices: alternate between the last two voices if we have at least 2 voices
      let selectedVoiceId: string;
      if (availableVoices.length >= 2 && samples.length > 0) {
        const lastSampleVoiceId = samples[samples.length - 1].voiceId;
        const lastVoiceIndex = availableVoices.findIndex(v => v.voiceId === lastSampleVoiceId);

        // Get the next voice, cycling back to the beginning if needed
        const nextVoiceIndex = (lastVoiceIndex + 1) % availableVoices.length;
        selectedVoiceId = availableVoices[nextVoiceIndex].voiceId;
      } else {
        // If we only have one voice or no samples yet, use the first voice
        selectedVoiceId = availableVoices[0].voiceId;
      }

      const newSample: MultiSpeakerSample = {
        id: `sample-${Date.now()}-${Math.random()}`,
        text: "",
        speakerName: t("speaker_name", { number: newSpeakerNumber }),
        voiceId: selectedVoiceId,
      };

      setSamples(prev => [...prev, newSample]);
      onDirty?.();
    }, [availableVoices, samples, t, onDirty]);

    const clearSamples = useCallback(() => {
      setSamples([]);
    }, []);

    const getSamplesMethod = useCallback(() => {
      return samples;
    }, [samples]);

    const setSamplesMethod = useCallback((newSamples: MultiSpeakerSample[]) => {
      setSamples(newSamples);
    }, []);

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        getSamples: getSamplesMethod,
        setSamples: setSamplesMethod,
        addSpeaker,
        clear: clearSamples,
      }),
      [getSamplesMethod, setSamplesMethod, addSpeaker, clearSamples],
    );

    if (availableVoices.length === 0) {
      return <div className={cn("p-24 text-center text-lighter", className)}>{tGlobal("loading")}</div>;
    }

    return (
      <div
        className={cn(
          "flex h-full w-full flex-1 flex-col gap-16 overflow-y-auto py-16",
          variant === "simple" && "py-0",
          className,
        )}
      >
        {/* Samples list */}
        <div className='flex flex-col'>
          {samples.map((sample, index) => (
            <SpeakerItem
              availableVoices={availableVoices}
              canRemove={samples.length > 1}
              index={index}
              key={sample.id}
              maxCharacters={maxCharacters}
              onRemove={removeSample}
              onUpdate={updateSample}
              sample={sample}
              textareaRef={textareaRefs.current[index]}
              variant={variant}
            />
          ))}
        </div>

        {/* Add speaker button */}
        <Button
          className={cn(
            "ml-12 flex h-32 items-center gap-4 bg-none py-8 normal-case",
            variant === "simple" && "ml-0 text-inverse",
          )}
          onClick={() => {
            addSpeaker();
            onDirty?.();
          }}
          size='sm'
          variant='elevated'
        >
          <div
            className={cn(
              "flex items-center justify-center rounded-full bg-darker p-2",
              variant === "simple" && "bg-none text-inverse",
            )}
          >
            <PlusIcon size={12} />
          </div>
          {t("add_speaker")}
        </Button>
      </div>
    );
  },
);

MultiSpeakerInput.displayName = "MultiSpeakerInput";
